// Updated ExecuteProductionOrder component with SFG consumed fields + extra delivery fields
import React, { useState } from "react";
import {
  Container,
  Row,
  Col,
  Form,
  Button,
  Spinner,
  Alert,
  Table,
  Modal,
  Accordion
} from "react-bootstrap";
import axios from "axios";
import { toast } from "react-toastify";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../Context/AuthContext";
import { Base_URL } from "../url";

export default function ExecuteProductionOrder() {
  const { productionOrderId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [queryOrderId, setQueryOrderId] = useState(productionOrderId ?? "");
  const [deliveryStatus, setDeliveryStatus] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [startDate, setStartDate] = useState("");
  const [startTime, setStartTime] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [executionRows, setExecutionRows] = useState([]);

  const [showDeliveryModal, setShowDeliveryModal] = useState(false);
  const [deliveryData, setDeliveryData] = useState([]);
  const [deliveryLoading, setDeliveryLoading] = useState(false);
  const [deliveryError, setDeliveryError] = useState("");

  const [itemMap, setItemMap] = useState({});
  const [actualOutputs, setActualOutputs] = useState({});
  const [stageStarts, setStageStarts] = useState({});

  // Map delivery index -> array of SFG consumed objects
  // { [deliveryIdx]: [{ itemCodeId, itemName, consumedQty }] }
  const [deliverySfgMap, setDeliverySfgMap] = useState({});

  // NEW: extra delivery fields per delivery row
  // { [idx]: { batchId, mrpInclGst, ptrExclGst, mfgDate, expDate } }
  const [deliveryExtras, setDeliveryExtras] = useState({});

  const EXECUTE_ENDPOINT = (id) =>
    `${Base_URL}/ProductionExecution/id?id=${encodeURIComponent(id)}`;
  const EXECUTION_FETCH = (id) =>
    `${Base_URL}/ProductionExecution/id?id=${encodeURIComponent(id)}`;
  const COMPLETE_STAGE_ENDPOINT = (id) =>
    `${Base_URL}/ProductionExecution/completeStage`;

  const validateBasic = (orderId) => {
    if (!startDate) {
      setError("Start date is required.");
      return false;
    }
    if (!startTime) {
      setError("Start time is required.");
      return false;
    }
    if (!orderId) {
      setError("Production order id is required.");
      return false;
    }
    if (!user?.userId) {
      setError("User not authenticated.");
      return false;
    }
    return true;
  };

  const fetchItemMap = async () => {
    try {
      const res = await axios.get(`${Base_URL}/Item/ItemGetData`);
      const itemsArr = Array.isArray(res?.data) ? res.data : [];
      const map = {};
      itemsArr.forEach((item) => {
        const key =
          item.itemCodeId ?? item.itemCode ?? item.item_code ?? item.itemCodeID ?? item.code;
        const name =
          item.itemName ?? item.itemDesc ?? item.description ?? item.name ?? "";
        if (key) map[key] = name;
      });
      setItemMap(map);
    } catch (err) {
      console.warn("Failed to fetch item map:", err);
    }
  };

  const fetchExecutionRows = async (id) => {
    if (!id) {
      setError("Enter a production order id and press Load Stages.");
      return;
    }

    setError("");
    setSuccessMsg("");
    setExecutionRows([]);
    setStageStarts({});
    try {
      setLoading(true);
      const res = await axios.get(EXECUTION_FETCH(id));
      const arr = Array.isArray(res?.data) ? res.data : [];
      setExecutionRows(arr);

      const initialStageStarts = {};
      arr.forEach((r, idx) => {
        initialStageStarts[idx] = {
          date: r.stageStartDate ?? "",
          time: r.stageStartTime ?? "",
          outputQty: r.stageOutputQty ?? "",
        };
      });
      setStageStarts(initialStageStarts);

      if (!res || Object.keys(itemMap).length === 0) {
        await fetchItemMap();
      }
    } catch (err) {
      console.error("Failed loading execution rows:", err);
      setError(
        err.response?.data?.message ?? err.message ?? "Failed to fetch execution rows"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleExecute = async (e) => {
    e?.preventDefault?.();
    setError("");
    setSuccessMsg("");
    setExecutionRows([]);

    const currentOrderId = queryOrderId || productionOrderId;
    if (!validateBasic(currentOrderId)) return;

    const sequences =
      executionRows.length > 0
        ? executionRows.map((r, idx) => ({
            ...r,
            sequenceNo: r.orderProductionProcessSeq ?? r.planProductionProcessSeq ?? idx + 1,
          }))
        : [];

    const payload = {
      productionOrderId: currentOrderId,
      startDate,
      startTime,
      createdBy: String(user?.userId),
      sequences,
    };

    try {
      setLoading(true);
      const res = await axios.post(EXECUTE_ENDPOINT(currentOrderId), payload);
      setSuccessMsg("Execution started successfully.");
      await fetchExecutionRows(currentOrderId);
      console.log("Execute response:", res.data);
    } catch (err) {
      console.error("Execute error:", err);
      setError(err.response?.data?.message ?? err.message ?? "Execution failed");
    } finally {
      setLoading(false);
    }
  };

  const updateStageStart = (rowIdx, field, value) => {
    setStageStarts((prev) => ({
      ...prev,
      [rowIdx]: { ...(prev[rowIdx] || { date: "", time: "", outputQty: "" }), [field]: value },
    }));
  };

  const handleCompleteStage = async (row, rowIdx) => {
    setError("");
    setSuccessMsg("");
    const currentOrderId = queryOrderId || productionOrderId;
    if (!currentOrderId) {
      setError("Missing production order id.");
      return;
    }

    const stageStart = stageStarts[rowIdx] ?? { date: "", time: "", outputQty: "" };

    if (!stageStart.date) {
      setError("Stage start date is required to complete the stage.");
      return;
    }
    if (!stageStart.time) {
      setError("Stage start time is required to complete the stage.");
      return;
    }

    const sequenceNo = row.orderProductionProcessSeq ?? row.planProductionProcessSeq ?? rowIdx + 1;
    const completePayload = {
      productionOrderId: currentOrderId,
      sequenceNo,
      sequence: {
        ...row,
        sequenceNo,
      },
      stageStartDate: stageStart.date,
      stageStartTime: stageStart.time,
      stageOutputQty: stageStart.outputQty ?? null,
      completedBy: String(user?.userId),
    };

    try {
      setLoading(true);
      const res = await axios.post(COMPLETE_STAGE_ENDPOINT(currentOrderId), completePayload);
      setSuccessMsg(`Stage ${sequenceNo} completed.`);
      await fetchExecutionRows(currentOrderId);
      console.log("Complete stage response:", res.data);
    } catch (err) {
      console.error("Complete stage error:", err);
      setError(err.response?.data?.message ?? err.message ?? "Failed to complete stage");
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async () => {
    try {
      const stages = await axios.get(
        `${Base_URL}/ProductionOrder/executeStages/id?id=${productionOrderId}`
      );
      const stagesData = stages.data;
      const stageCount = Array.isArray(stagesData) ? stagesData.length : 0;
      let completeCount = 0;
      (stagesData || []).forEach((s) => {
        if (s.stageStatus === "Complete") completeCount = completeCount + 1;
      });
      if (stageCount === completeCount && stageCount !== 0 && completeCount !== 0) setDeliveryStatus(true);
      else setDeliveryStatus(false);
    } catch (err) {
      console.error("Verify error:", err);
      setError(err.response?.data?.message ?? err.message ?? "Failed to verify stages");
    }
  };

  // helper: derive SFGs from executionRows (unique outputs excluding given delivery item code)
  const deriveSfgsForDelivery = (deliveryItemCode) => {
    const sfgSet = new Map();
    (executionRows || []).forEach((r) => {
      const code =
        r.orderStgOutputItem ??
        r.planStgOutputItem ??
        r.itemCodeId ??
        r.outputItemCode ??
        null;
      if (!code) return;
      if (deliveryItemCode && String(code) === String(deliveryItemCode)) return; // skip same as delivery
      if (!sfgSet.has(code)) {
        sfgSet.set(code, {
          itemCodeId: code,
          itemName: itemMap[code] ?? code,
          consumedQty: "", // empty by default
        });
      }
    });
    return Array.from(sfgSet.values());
  };

  // handleDelivery now initializes deliverySfgMap and deliveryExtras per delivery row
  const handleDelivery = async () => {
    setDeliveryError("");
    setDeliveryData([]);
    setItemMap({});
    setActualOutputs({});
    setDeliverySfgMap({});
    setDeliveryExtras({});

    const currentOrderId = queryOrderId || productionOrderId;
    if (!currentOrderId) {
      setDeliveryError("Missing production order id to fetch delivery data.");
      return;
    }

    try {
      setDeliveryLoading(true);
      const res = await axios.get(
        `${Base_URL}/ProductionOrder/orderOutput/id?id=${encodeURIComponent(currentOrderId)}`
      );
      const arr = Array.isArray(res?.data) ? res.data : [];
      setDeliveryData(arr);

      // fetch item info to map itemCodeId -> itemName
      try {
        const itemsRes = await axios.get(`${Base_URL}/Item/ItemGetData`);
        const itemsArr = Array.isArray(itemsRes?.data) ? itemsRes.data : [];
        const map = {};
        itemsArr.forEach((item) => {
          const key =
            item.itemCodeId ?? item.itemCode ?? item.item_code ?? item.itemCodeID ?? item.code;
          const name =
            item.itemName ??
            item.itemDesc ??
            item.description ??
            item.name ??
            (item.outputItem && item.outputItem.itemName) ??
            "";
          if (key) map[key] = name;
        });
        setItemMap(map);
      } catch (itemsErr) {
        console.warn("Failed to fetch item names:", itemsErr);
      }

      // Build deliverySfgMap: for each delivery row, attempt to find SFGs
      const sfgMap = {};
      const extrasInit = {};

      arr.forEach((d, idx) => {
        // 1) prefer explicit sfg list from API if present
        const possibleSfgs = d.sfgItems ?? d.consumedItems ?? d.sfgConsumed ?? null;
        if (Array.isArray(possibleSfgs) && possibleSfgs.length > 0) {
          sfgMap[idx] = possibleSfgs.map((s) => ({
            itemCodeId: s.itemCodeId ?? s.itemCode ?? s.code ?? (s.outputItem && s.outputItem.itemCodeId) ?? "",
            itemName:
              s.itemName ??
              s.itemDesc ??
              s.name ??
              (s.outputItem && s.outputItem.itemName) ??
              (itemMap[s.itemCodeId] ?? ""),
            consumedQty: s.consumedQty ?? s.qty ?? "",
          }));
        } else {
          // 2) fallback: derive unique stage outputs from executionRows excluding this delivery item code
          const derived = deriveSfgsForDelivery(d.itemCodeId);
          sfgMap[idx] = derived;
        }

        // init extra fields from API if already available
        extrasInit[idx] = {
          batchId: d.batchId ?? d.batch_id ?? "",
          mrpInclGst: d.mrpIncludingGst ?? d.mrpInclGst ?? "",
          ptrExclGst: d.ptrExcludingGst ?? d.ptrExclGst ?? "",
          mfgDate: d.mfgDate ?? "",
          expDate: d.expDate ?? "",
        };
      });

      setDeliverySfgMap(sfgMap);
      setDeliveryExtras(extrasInit);

      setShowDeliveryModal(true);
      console.log("Delivery data:", arr);
    } catch (err) {
      console.error("Delivery error:", err);
      setDeliveryError(err.response?.data?.message ?? err.message ?? "Failed to fetch delivery data");
    } finally {
      setDeliveryLoading(false);
    }
  };

  const updateActualOutput = (idx, value) => {
    setActualOutputs((prev) => ({ ...prev, [idx]: value }));
  };

  // update consumed qty for a specific delivery's sfg entry
  const updateDeliverySfgQty = (deliveryIdx, sfgIdx, value) => {
    setDeliverySfgMap((prev) => {
      const copy = { ...prev };
      const arr = Array.isArray(copy[deliveryIdx]) ? [...copy[deliveryIdx]] : [];
      const entry = { ...(arr[sfgIdx] || { itemCodeId: "", itemName: "", consumedQty: "" }) };
      entry.consumedQty = value;
      arr[sfgIdx] = entry;
      copy[deliveryIdx] = arr;
      return copy;
    });
  };

  // NEW: update extra fields (batch, mrp, ptr, dates)
  const updateDeliveryExtra = (idx, field, value) => {
    setDeliveryExtras((prev) => ({
      ...prev,
      [idx]: {
        ...(prev[idx] || {
          batchId: "",
          mrpInclGst: "",
          ptrExclGst: "",
          mfgDate: "",
          expDate: ""
        }),
        [field]: value,
      },
    }));
  };

  // Confirm adds sfgConsumed array + extra fields per delivery row
  const handleConfirmDelivery = async () => {
    const payload = (deliveryData || []).map((d, idx) => {
      const sfgConsumed = (deliverySfgMap[idx] || []).map((s) => ({
        itemCodeId: s.itemCodeId,
        itemName: s.itemName ?? itemMap[s.itemCodeId] ?? "",
        consumedQty:
          s.consumedQty !== undefined && s.consumedQty !== "" ? Number(s.consumedQty) : 0,
      }));

      const extra = deliveryExtras[idx] || {};

      return {
        productionOrderId: d.productionOrderId,
        itemCodeId: d.itemCodeId,
        itemName: itemMap[d.itemCodeId] ?? d.itemName ?? null,
        productionOrderQty: Number(d.productionOrderQty ?? 0),
        productionOrderQtyUnit: d.productionOrderQtyUnit ?? "",
        actualOutputQty:
          actualOutputs[idx] !== undefined && actualOutputs[idx] !== ""
            ? Number(actualOutputs[idx])
            : 0,
        createdBy: String(user?.userId ?? ""),
        organisationId: Number(user?.organisationId ?? 0),
        sfgConsumed,
        // NEW: extra fields in payload
        batchId: extra.batchId || null,
        mrpIncludingGst: extra.mrpInclGst !== "" ? Number(extra.mrpInclGst) : 0,
        ptrExcludingGst: extra.ptrExclGst !== "" ? Number(extra.ptrExclGst) : 0,
        mfgDate: extra.mfgDate || null,
        expDate: extra.expDate || null,
      };
    });

    console.log("Confirm delivery payload (sent):", payload);
    const body = { order: payload };
    setConfirmLoading(true);
    try {
      const res = await axios.post(
        `${Base_URL}/ProductionOrder/deliverOrder`,
        body,
        { headers: { "Content-Type": "application/json" } }
      );
      console.log(body);
      toast.success("Order delivered successfully!");
      setSuccessMsg("Delivery confirmed successfully.");
    } catch (err) {
      console.error("Delivery confirm error:", err);
      toast.error(err.response?.data?.message ?? "Failed to deliver order");
    } finally {
      setConfirmLoading(false);
    }
  };

  return (
    <div className="main-content expand">
      <Container fluid className="fields-container">
        <h3 className="fields-title">
          Execute Production Order — {productionOrderId || "(typed below)"}
        </h3>

        <div className="mb-3 d-flex gap-2 align-items-end">
          <Form.Group className="mb-0" style={{ minWidth: 320 }}>
            <Form.Label>Production Order ID</Form.Label>
            <div className="d-flex gap-2">
              <Form.Control
                type="text"
                placeholder="Enter production order id (e.g. PRO0000001)"
                value={queryOrderId}
                onChange={(e) => setQueryOrderId(e.target.value)}
              />
              <Button
                variant="outline-primary"
                size="sm"
                onClick={() => fetchExecutionRows(queryOrderId)}
                disabled={loading || !queryOrderId}
                title="Load stages for this order"
              >
                {loading ? <Spinner animation="border" size="sm" /> : "Load Stages"}
              </Button>
            </div>
          </Form.Group>

          <div style={{ marginLeft: "auto" }}>
            <Button
              onClick={() => {
                setStartDate("");
                setStartTime("");
                setError("");
                setSuccessMsg("");
                setExecutionRows([]);
                setStageStarts({});
                setQueryOrderId(productionOrderId ?? "");
              }}
              size="sm"
              variant="secondary"
              disabled={loading}
              className="me-2"
            >
              Reset
            </Button>

            <Button onClick={handleExecute} size="sm" variant="primary" disabled={loading}>
              {loading ? (
                <>
                  <Spinner animation="border" size="sm" /> Executing...
                </>
              ) : (
                "Start Execution"
              )}
            </Button>
          </div>
        </div>

        {error && (
          <Alert variant="danger" onClose={() => setError("")} dismissible>
            {error}
          </Alert>
        )}
        {successMsg && (
          <Alert variant="success" onClose={() => setSuccessMsg("")} dismissible>
            {successMsg}
          </Alert>
        )}

        <Form onSubmit={handleExecute}>
          <Row className="g-3">
            <Col md={4}>
              <Form.Group>
                <Form.Label>Start Date</Form.Label>
                <Form.Control type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
              </Form.Group>
            </Col>

            <Col md={4}>
              <Form.Group>
                <Form.Label>Start Time</Form.Label>
                <Form.Control type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} />
              </Form.Group>
            </Col>

            <Col md={4}>
              <Form.Group>
                <Form.Label>User (readonly)</Form.Label>
                <Form.Control type="text" readOnly value={user?.userId ? String(user.userId) : "Not signed in"} />
              </Form.Group>
            </Col>
          </Row>

          <Row className="mt-3">
            <Col>
              <small className="text-muted">
                Payload posted will include: productionOrderId (typed above or from route), startDate, startTime,
                createdBy and sequences (if present).
              </small>
            </Col>
          </Row>
        </Form>

        {executionRows.length > 0 && (
          <div className="mt-4 border rounded p-3">
            <h6 className="mb-3">Execution Steps</h6>
            <Table bordered hover size="sm" responsive>
              <thead>
                <tr>
                  <th>Step</th>
                  <th>Process</th>
                  <th>Subprocess</th>
                  <th>Station</th>
                  <th>Duration</th>
                  <th>SFG</th>
                  <th>Item Name</th>
                  <th>SFG_ITEM_QTY</th>
                  <th>SFG_ITEM_UNIT</th>
                  <th>Stage Output Qty</th>
                  <th>Stage Start Date</th>
                  <th>Stage Start Time</th>
                  <th>Action</th>
                </tr>
              </thead>

              <tbody>
                {executionRows.map((r, i) => {
                  const seqNo = r.orderProductionProcessSeq ?? r.planProductionProcessSeq ?? i + 1;
                  const stageStart = stageStarts[i] ?? { date: "", time: "", outputQty: "" };
                  const itemCode = r.orderStgOutputItem ?? r.planStgOutputItem ?? r.itemCodeId ?? r.outputItemCode ?? "";
                  const itemName = (itemCode && itemMap[itemCode]) ? itemMap[itemCode] : (itemCode || "-");

                  return (
                    <tr key={`${r.planProductionOrderId || queryOrderId || productionOrderId}-${i}`}>
                      <td>{seqNo}</td>
                      <td>{r.orderProductionProcessId ?? r.planProductionProcessId ?? "-"}</td>
                      <td>{r.orderProductionSubprocessId ?? r.planProductionSubprocessId ?? "-"}</td>
                      <td>{r.orderProductionStationId ?? r.planProductionStationId ?? "-"}</td>
                      <td>
                        {(r.orderApproxDuration ?? r.planApproxDuration) ?? "-"}
                        {(r.orderApproxDurationUnit ?? r.planApproxDurationUnit)
                          ? ` ${r.orderApproxDurationUnit ?? r.planApproxDurationUnit}`
                          : ""}
                      </td>

                      <td>{r.orderStgOutputItem ?? r.planStgOutputItem ?? "-"}</td>
                      <td>{itemName}</td>
                      <td>{(r.orderStgOutputQty ?? r.planStgOutputQty) ?? "-"}</td>
                      <td>
                        {(r.orderStgOutputUnit ?? r.planStgOutputUnit)
                          ? ` ${r.orderStgOutputUnit ?? r.planStgOutputUnit}`
                          : ""}
                      </td>

                      <td style={{ minWidth: 140 }}>
                        <Form.Control
                          type="number"
                          step="any"
                          value={stageStart.outputQty ?? ""}
                          onChange={(e) => updateStageStart(i, "outputQty", e.target.value)}
                        />
                      </td>

                      <td style={{ minWidth: 140 }}>
                        <Form.Control
                          type="date"
                          value={stageStart.date}
                          onChange={(e) => updateStageStart(i, "date", e.target.value)}
                        />
                      </td>
                      <td style={{ minWidth: 120 }}>
                        <Form.Control
                          type="time"
                          value={stageStart.time}
                          onChange={(e) => updateStageStart(i, "time", e.target.value)}
                        />
                      </td>

                      <td className="text-end">
                        <Button
                          size="sm"
                          variant="success"
                          onClick={() => handleCompleteStage(r, i)}
                          disabled={r.prdOrdseqActive === false}
                        >
                          {loading ? <Spinner animation="border" size="sm" /> : "Complete Stage"}
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </div>
        )}

        <div className="mt-3 d-flex gap-2">
          <Button onClick={handleVerify}>Verify Stages</Button>
          <Button onClick={handleDelivery} disabled={!deliveryStatus || deliveryLoading}>
            {deliveryLoading ? <Spinner animation="border" size="sm" /> : "Deliver Order"}
          </Button>
        </div>

        {/* Delivery Modal */}
        <Modal
          show={showDeliveryModal}
          onHide={() => setShowDeliveryModal(false)}
          size="lg"
          centered
        >
          <Modal.Header closeButton>
            <Modal.Title>
              Delivery / Order Output — {queryOrderId || productionOrderId}
            </Modal.Title>
          </Modal.Header>

          <Modal.Body>
            {deliveryError && <Alert variant="danger">{deliveryError}</Alert>}

            {!deliveryError && deliveryLoading && (
              <div className="text-center py-3">
                <Spinner animation="border" />
              </div>
            )}

            {!deliveryLoading && Array.isArray(deliveryData) && deliveryData.length === 0 && (
              <Alert variant="info">No delivery data returned from the API.</Alert>
            )}

            {!deliveryLoading && Array.isArray(deliveryData) && deliveryData.length > 0 && (
              <div style={{ maxHeight: "65vh", overflowY: "auto", paddingRight: 6 }}>
                {/* Use Accordion so each delivery item can be collapsed */}
                <Accordion defaultActiveKey={deliveryData.length === 1 ? "0" : null}>
                  {deliveryData.map((d, idx) => {
                    const sfgList = deliverySfgMap[idx] ?? [];
                    const itemName = itemMap[d.itemCodeId] ?? d.itemName ?? "-";
                    const extra = deliveryExtras[idx] || {
                      batchId: "",
                      mrpInclGst: "",
                      ptrExclGst: "",
                      mfgDate: "",
                      expDate: ""
                    };

                    return (
                      <Accordion.Item
                        eventKey={String(idx)}
                        key={`${d.productionOrderId}-${d.itemCodeId}-${idx}`}
                      >
                        <Accordion.Header>
                          <div className="w-100 d-flex align-items-center justify-content-between">
                            <div className="d-flex align-items-center gap-3">
                              <div style={{ minWidth: 32, textAlign: "center", fontWeight: 600 }}>
                                {idx + 1}
                              </div>

                              <div>
                                <div style={{ fontWeight: 600 }}>
                                  <span className="me-2">{d.itemCodeId ?? "-"}</span>
                                  <small className="text-muted">/</small>
                                  <span
                                    className="ms-2 text-truncate"
                                    style={{ maxWidth: 280, display: "inline-block" }}
                                  >
                                    {itemName}
                                  </span>
                                </div>
                                <div className="small text-muted">
                                  Order Qty: <strong>{d.productionOrderQty ?? "-"}</strong>
                                  {d.productionOrderQtyUnit ? (
                                    <span className="ms-2 badge bg-light text-dark">
                                      {d.productionOrderQtyUnit}
                                    </span>
                                  ) : null}
                                </div>
                              </div>
                            </div>

                            {/* Actual output quick-edit on header */}
                            <div className="d-flex align-items-center gap-2">
                              <Form.Control
                                size="sm"
                                type="number"
                                step="any"
                                placeholder="Actual"
                                value={actualOutputs[idx] ?? ""}
                                onClick={(e) => e.stopPropagation()} // prevent accordion toggle when focusing
                                onChange={(e) => updateActualOutput(idx, e.target.value)}
                                style={{ width: 120 }}
                              />
                              <Button
                                size="sm"
                                variant="outline-secondary"
                                onClick={(ev) => {
                                  ev.stopPropagation(); // avoid toggling
                                  alert(
                                    "You can edit SFG consumed and extra details inside the expanded panel."
                                  );
                                }}
                              >
                                SFG
                              </Button>
                            </div>
                          </div>
                        </Accordion.Header>

                        <Accordion.Body
                          style={{ background: "#fbfbfb", borderRadius: 6 }}
                        >
                          <Row className="mb-2">
                            <Col xs={12} md={6}>
                              <div className="small text-muted">Production Order</div>
                              <div style={{ fontWeight: 600 }}>
                                {d.productionOrderId ?? "-"}
                              </div>
                            </Col>
                            <Col
                              xs={12}
                              md={6}
                              className="text-md-end mt-2 mt-md-0"
                            >
                              <div className="small text-muted">Item</div>
                              <div style={{ fontWeight: 600 }}>
                                {d.itemCodeId ?? "-"} —{" "}
                                <span className="text-muted">{itemName}</span>
                              </div>
                            </Col>
                          </Row>

                          {/* NEW: Extra fields row */}
                          <Row className="mb-3">
                            <Col md={4} className="mb-2">
                              <Form.Group>
                                <Form.Label className="small mb-1">
                                  Batch ID
                                </Form.Label>
                                <Form.Control
                                  size="sm"
                                  type="text"
                                  value={extra.batchId}
                                  onChange={(e) =>
                                    updateDeliveryExtra(idx, "batchId", e.target.value)
                                  }
                                  placeholder="Batch ID"
                                />
                              </Form.Group>
                            </Col>

                            <Col md={4} className="mb-2">
                              <Form.Group>
                                <Form.Label className="small mb-1">
                                  MRP Including GST
                                </Form.Label>
                                <Form.Control
                                  size="sm"
                                  type="number"
                                  step="any"
                                  value={extra.mrpInclGst}
                                  onChange={(e) =>
                                    updateDeliveryExtra(idx, "mrpInclGst", e.target.value)
                                  }
                                  placeholder="MRP Incl. GST"
                                />
                              </Form.Group>
                            </Col>

                            <Col md={4} className="mb-2">
                              <Form.Group>
                                <Form.Label className="small mb-1">
                                  PTR Excluding GST
                                </Form.Label>
                                <Form.Control
                                  size="sm"
                                  type="number"
                                  step="any"
                                  value={extra.ptrExclGst}
                                  onChange={(e) =>
                                    updateDeliveryExtra(idx, "ptrExclGst", e.target.value)
                                  }
                                  placeholder="PTR Excl. GST"
                                />
                              </Form.Group>
                            </Col>
                          </Row>

                          <Row className="mb-3">
                            <Col md={6} className="mb-2">
                              <Form.Group>
                                <Form.Label className="small mb-1">
                                  Mfg Date
                                </Form.Label>
                                <Form.Control
                                  size="sm"
                                  type="date"
                                  value={extra.mfgDate}
                                  onChange={(e) =>
                                    updateDeliveryExtra(idx, "mfgDate", e.target.value)
                                  }
                                />
                              </Form.Group>
                            </Col>

                            <Col md={6} className="mb-2">
                              <Form.Group>
                                <Form.Label className="small mb-1">
                                  Exp Date
                                </Form.Label>
                                <Form.Control
                                  size="sm"
                                  type="date"
                                  value={extra.expDate}
                                  onChange={(e) =>
                                    updateDeliveryExtra(idx, "expDate", e.target.value)
                                  }
                                />
                              </Form.Group>
                            </Col>
                          </Row>

                          <div className="mb-2">
                            <strong className="small">
                              SFG Items Consumed
                            </strong>
                            <div className="small text-muted">
                              Enter the quantity of each SFG consumed for this
                              delivery item.
                            </div>
                          </div>

                          {sfgList.length === 0 ? (
                            <div className="p-3 border rounded bg-white small text-muted">
                              No SFG items detected for this delivery item.
                            </div>
                          ) : (
                            <Table
                              size="sm"
                              bordered
                              responsive
                              className="bg-white"
                            >
                              <thead>
                                <tr>
                                  <th
                                    style={{ width: 40 }}
                                    className="text-center"
                                  >
                                    #
                                  </th>
                                  <th style={{ minWidth: 150 }}>Item Code</th>
                                  <th>Item Name</th>
                                  <th style={{ width: 180 }}>Consumed Qty</th>
                                </tr>
                              </thead>
                              <tbody>
                                {sfgList.map((s, sIdx) => (
                                  <tr
                                    key={`${d.productionOrderId}-sfg-${s.itemCodeId}-${sIdx}`}
                                  >
                                    <td className="text-center align-middle">
                                      {sIdx + 1}
                                    </td>
                                    <td className="align-middle">
                                      <div style={{ fontWeight: 600 }}>
                                        {s.itemCodeId ?? "-"}
                                      </div>
                                    </td>
                                    <td className="align-middle">
                                      <div className="text-muted">
                                        {s.itemName ??
                                          itemMap[s.itemCodeId] ??
                                          "-"}
                                      </div>
                                    </td>
                                    <td className="align-middle">
                                      <div className="d-flex gap-2">
                                        <Form.Control
                                          size="sm"
                                          type="number"
                                          step="any"
                                          value={s.consumedQty ?? ""}
                                          onChange={(e) =>
                                            updateDeliverySfgQty(
                                              idx,
                                              sIdx,
                                              e.target.value
                                            )
                                          }
                                          placeholder="Consumed qty"
                                          style={{ maxWidth: 160 }}
                                        />
                                        <div className="d-none d-md-block align-self-center small text-muted">
                                          unit
                                        </div>
                                      </div>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </Table>
                          )}

                          <div className="d-flex justify-content-between align-items-center mt-3">
                            <div className="small text-muted">
                              Tip: verify SFG quantities and extra details
                              before confirming delivery.
                            </div>
                          </div>
                        </Accordion.Body>
                      </Accordion.Item>
                    );
                  })}
                </Accordion>
              </div>
            )}
          </Modal.Body>

          <Modal.Footer className="justify-content-between">
            <div className="small text-muted">
              Showing {Array.isArray(deliveryData) ? deliveryData.length : 0} delivery item(s)
            </div>

            <div>
              <Button
                variant="secondary"
                onClick={() => setShowDeliveryModal(false)}
              >
                Close
              </Button>
              <Button
                variant="primary"
                className="ms-2"
                onClick={handleConfirmDelivery}
                disabled={deliveryLoading || confirmLoading}
              >
                {confirmLoading ? (
                  <Spinner animation="border" size="sm" />
                ) : (
                  "Confirm"
                )}
              </Button>
            </div>
          </Modal.Footer>
        </Modal>
      </Container>
    </div>
  );
}
